/**
 * IncomingCallModal.js - Modal for incoming call notifications
 * Shows caller info and accept/decline buttons
 */
import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, PhoneOff, Video, User, X } from 'lucide-react';
import { Button } from '../ui/button';

const IncomingCallModal = ({
  isOpen,
  caller,
  callType = 'video',
  onAccept,
  onDecline
}) => {
  const ringtoneRef = useRef(null);

  // Play ringtone when modal opens
  useEffect(() => {
    if (isOpen && ringtoneRef.current) {
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.loop = true;
    }

    return () => {
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current.currentTime = 0;
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100] p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-gradient-to-b from-[var(--background-paper)] to-[var(--background-elevated)] rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl"
        >
          {/* Animated background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-green-500/20 via-transparent to-green-500/20"
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            />
          </div>

          <div className="relative p-8 text-center space-y-6">
            {/* Call type indicator */}
            <div className="flex justify-center">
              <motion.div
                className="w-6 h-6 bg-green-500 rounded-full"
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
            </div>

            {/* Caller info */}
            <div className="space-y-4">
              {/* Avatar */}
              <motion.div
                className="w-28 h-28 mx-auto rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center shadow-lg"
                animate={{ 
                  boxShadow: [
                    '0 0 0 0 rgba(34, 197, 94, 0.4)',
                    '0 0 0 20px rgba(34, 197, 94, 0)',
                  ]
                }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <User className="w-14 h-14 text-white" />
              </motion.div>

              {/* Name */}
              <div>
                <h2 className="text-2xl font-bold">{caller?.name || 'Unknown'}</h2>
                <p className="text-[var(--text-muted)] capitalize">{caller?.role || 'User'}</p>
              </div>

              {/* Call type */}
              <div className="flex items-center justify-center gap-2 text-green-500">
                {callType === 'video' ? (
                  <Video className="w-5 h-5" />
                ) : (
                  <Phone className="w-5 h-5" />
                )}
                <span className="text-sm font-medium">
                  Incoming {callType === 'video' ? 'Video' : 'Voice'} Call
                </span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex justify-center gap-8 pt-4">
              {/* Decline */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onDecline}
                className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg"
                data-testid="decline-call-btn"
              >
                <PhoneOff className="w-7 h-7" />
              </motion.button>

              {/* Accept */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onAccept}
                className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center shadow-lg"
                data-testid="accept-call-btn"
              >
                {callType === 'video' ? (
                  <Video className="w-7 h-7" />
                ) : (
                  <Phone className="w-7 h-7" />
                )}
              </motion.button>
            </div>

            {/* Swipe hint */}
            <p className="text-xs text-[var(--text-muted)]">
              Swipe up to answer
            </p>
          </div>

          {/* Ringtone audio */}
          <audio
            ref={ringtoneRef}
            src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleVopPJ/NrYJGGiqOn8eydzMiOImXuJOASDYoTnBziZKHgXdySUY+TFtmeYWEf3pxaF1YUlhfa3V9gIB8eXdxb2xqZmVnam5xcXJxcnNzcnFwcHBxcXJycnJycnJycnJycXFxcXFxcXFxcXFxcXFxcXFxcXFxcXFxcXFxcXFxcXFxcXFx"
            preload="auto"
          />
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default IncomingCallModal;
